import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Eyebrow from "../components/Eyebrow";
import Breadcrumbs from "../components/Breadcrumbs";
import CTAButton from "../components/CTAButton";
import FAQAccordion from "../components/FAQAccordion";
import FacilityGallery, { GalleryPending } from "../components/FacilityGallery";
import { Reveal, KineticLines } from "../components/Reveal";
import { usePageMeta } from "../lib/seo";
import { SITE, FACILITIES, FAQS, COMPENSATION_DISCLOSURE } from "../lib/site";

const BOARD_AND_CARE_FAQS = [
  FAQS.find((f) => f.q.startsWith("How do you vet")),
  FAQS.find((f) => f.q.startsWith("What types of senior care")),
  FAQS.find((f) => f.q.startsWith("What areas do you serve")),
].filter(Boolean);

function FacilityCard({ facility }) {
  return (
    <Reveal>
      <article
        id={facility.id}
        data-testid={`facility-card-${facility.id}`}
        className="group flex h-full scroll-mt-28 flex-col overflow-hidden rounded-2xl border border-inkline bg-warmwhite"
      >
        {facility.photos.length > 0 ? (
          <FacilityGallery facility={facility} />
        ) : (
          <GalleryPending facilityId={facility.id} />
        )}
        <div className="flex flex-1 flex-col p-6">
          <p className="font-serif text-lg text-clay" aria-hidden="true">
            {String(facility.num).padStart(2, "0")}
          </p>
          <h3 className="mt-1 font-serif text-[1.35rem] font-medium leading-snug text-forest">
            {facility.name}
          </h3>
          <p className="mt-1 text-[0.9rem] text-charcoal/65">
            {facility.location}, California
          </p>
          <dl className="mt-4 space-y-1.5 text-[0.85rem] text-charcoal/75">
            {facility.rooms && (
              <div className="flex gap-2">
                <dt className="shrink-0 text-forest/60">Rooms:</dt>
                <dd>Private &amp; shared rooms</dd>
              </div>
            )}
          </dl>
          <Link
            to={`/contact?home=${encodeURIComponent(facility.name)}`}
            data-testid={`facility-cta-${facility.id}`}
            onClick={() => trackEventFacility(facility.name)}
            className="mt-auto inline-flex items-center gap-2 pt-5 text-[0.92rem] font-medium text-forest transition-colors hover:text-clay"
          >
            Ask CareCraft About This Home
            <ArrowRight
              className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
              aria-hidden="true"
            />
          </Link>
        </div>
      </article>
    </Reveal>
  );
}

function trackEventFacility(name) {
  import("../lib/analytics").then((m) => m.trackEvent("facility_inquiry_click", { facility: name }));
}

export default function BoardAndCare() {
  usePageMeta({
    title: "Board & Care Homes in Los Angeles",
    description:
      "Explore personally vetted Board & Care homes in Los Angeles: smaller, home-like residential settings with day-to-day support. CareCraft's guidance is complimentary to families.",
    path: "/board-and-care",
  });

  return (
    <>
      <section className="bg-ivory pt-[72px]">
        <div className="mx-auto max-w-site px-4 pb-14 pt-10 sm:px-6 md:pb-16 md:pt-14 lg:px-12">
          <Breadcrumbs trail={[{ label: "Board & Care" }]} />
          <div className="mt-10 max-w-3xl">
            <Eyebrow>Board &amp; Care</Eyebrow>
            <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.15] tracking-tight text-forest sm:text-5xl lg:text-[3.6rem]">
              <KineticLines lines={[<>Board &amp; Care</>]} />
            </h1>
            <p className="mt-5 font-serif text-2xl italic leading-snug text-forest/85 md:text-[1.7rem]">
              A more personal, home-like setting for everyday support.
            </p>
            <Reveal delay={0.35}>
              <p className="mt-6 max-w-2xl text-lg leading-[1.7] text-charcoal/80">
                Board &amp; Care offers a smaller residential setting where older adults can receive
                day-to-day assistance in a more intimate, home-like environment.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-warmwhite py-16 md:py-20">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          <Reveal>
            <h2 className="font-serif text-3xl font-medium tracking-tight text-forest md:text-4xl">
              Board &amp; Care homes to explore
            </h2>
            <p className="mt-4 max-w-2xl text-lg text-charcoal/75">
              A selection of personally vetted residential care homes CareCraft can help your family
              consider.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {FACILITIES.map((f) => (
              <FacilityCard key={f.id} facility={f} />
            ))}
          </div>
          <Reveal>
            <p className="mt-10 max-w-3xl text-[0.9rem] leading-relaxed text-charcoal/70">
              Private and shared room options are available. Room availability may vary. Contact
              CareCraft to ask about current options.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory py-20 md:py-28">
        <div className="mx-auto grid max-w-site gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow>About this option</Eyebrow>
              <h2 className="mt-6 font-serif text-3xl font-medium leading-tight tracking-tight text-forest md:text-4xl">
                Understanding Board &amp; Care
              </h2>
              <p className="mt-6 text-lg leading-[1.7] text-charcoal/80">
                A smaller residential setting, often in a home-like environment, for older adults who
                need day-to-day assistance and may benefit from a more intimate setting and closer
                familiarity with staff. In California, many Board &amp; Care homes and Assisted Living
                communities operate under the Residential Care Facility for the Elderly licensing
                framework, although their size and setting can be quite different.
              </p>
            </Reveal>
          </div>
          <div className="grid gap-10 lg:col-span-7">
            <Reveal delay={0.08}>
              <div>
                <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-forest/60">
                  Who might consider it
                </p>
                <p className="mt-3 leading-[1.7] text-charcoal/80">
                  Often considered by families who want a smaller, home-like setting with closer
                  familiarity with staff and a high level of personal attention.
                </p>
              </div>
              <div className="mt-8">
                <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-forest/60">
                  The support concept
                </p>
                <p className="mt-3 leading-[1.7] text-charcoal/80">
                  Day-to-day assistance in a residential home, with a more intimate living
                  environment and a small number of residents.
                </p>
              </div>
              <div className="mt-8">
                <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-forest/60">
                  Questions to ask when visiting
                </p>
                <ul className="mt-4 space-y-3">
                  {[
                    "How many residents live here, and what is the staffing like day to day?",
                    "What does a typical day look like?",
                    "How are care plans adjusted as needs change?",
                    "What's included in the monthly cost, and what isn't?",
                  ].map((q) => (
                    <li key={q} className="flex gap-3 leading-[1.6] text-charcoal/80">
                      <span className="mt-[0.6em] h-1.5 w-1.5 shrink-0 rounded-full bg-clay" aria-hidden="true" />
                      {q}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-warmwhite py-20 md:py-24">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          <Reveal>
            <Eyebrow>Good to know</Eyebrow>
            <h2 className="mt-6 max-w-2xl font-serif text-3xl font-medium tracking-tight text-forest md:text-4xl">
              Questions about Board &amp; Care
            </h2>
          </Reveal>
          <div className="mt-10 max-w-4xl">
            <FAQAccordion items={BOARD_AND_CARE_FAQS} />
          </div>
          <Reveal>
            <p className="mt-8 max-w-3xl text-[0.9rem] leading-[1.7] text-charcoal/70">
              {COMPENSATION_DISCLOSURE}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="grain bg-forest py-20 text-ivory md:py-24">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          <Reveal>
            <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
              <div>
                <h2 className="max-w-xl font-serif text-3xl font-medium leading-tight md:text-4xl">
                  Would you like to visit one of these homes?
                </h2>
                <p className="mt-4 max-w-xl text-ivory/80">
                  Tell us what matters most, and CareCraft will help you explore the homes and
                  coordinate a visit.
                </p>
              </div>
              <CTAButton to="/contact" variant="ivory" testId="board-and-care-final-cta">
                Talk With Doris
              </CTAButton>
            </div>
          </Reveal>
          <p className="mt-10 text-[0.85rem] text-ivory/60">
            Questions? Call {SITE.phoneDisplay} or email {SITE.email}.
          </p>
        </div>
      </section>
    </>
  );
}
